import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { customToast } from '../utils/toastUtils';
import { useUser } from '../contexts/UserContext';
import CustomerSupportModal from '../components/CustomerSupportModal';
import TicketChatModal from '../components/TicketChatModal';
import './CustomerSupport.css';

const CustomerSupport = ({ onFaqClick, onLoginClick }) => {
  const navigate = useNavigate();
  const { userData } = useUser();
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedIssue, setSelectedIssue] = useState('');
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  const fetchTickets = useCallback(async () => {
    if (!userData?.uid) return;
    setTicketsLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/api/email/support/tickets/${encodeURIComponent(userData.uid)}`
      );
      const result = await response.json();
      if (result.success) {
        setTickets(result.tickets);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setTicketsLoading(false);
    }
  }, [userData?.uid]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowChatModal(true);
  };

  const handleTicketUpdated = () => {
    fetchTickets();
    // Refresh the selected ticket data
    if (selectedTicket) {
      const updated = tickets.find(t => t.id === selectedTicket.id);
      if (updated) setSelectedTicket(updated);
    }
  };

  // Hide closed tickets after 30 minutes of being closed
  const visibleTickets = tickets.filter(t => {
    if (t.status === 'closed') {
      const closedTime = t.closedAt || t.updatedAt;
      if (closedTime) {
        const closedMs = new Date(closedTime).getTime();
        const now = Date.now();
        const thirtyMinMs = 30 * 60 * 1000;
        if (now - closedMs > thirtyMinMs) return false;
      }
    }
    return true;
  });

  const filteredTickets = activeFilter === 'all'
    ? visibleTickets
    : visibleTickets.filter(t => t.status === activeFilter);

  const getStatusInfo = (status) => {
    switch (status) {
      case 'open': return { bg: '#dcfce7', text: '#166534', border: '#86efac', label: 'Open', icon: '🟢' };
      case 'in-progress': return { bg: '#fef3c7', text: '#92400e', border: '#fbbf24', label: 'In Progress', icon: '🟡' };
      case 'closed': return { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5', label: 'Closed', icon: '🔴' };
      default: return { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1', label: status, icon: '⚪' };
    }
  };

  const formatTicketDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleIssueClick = (categoryTitle, issue) => {
    // Guard: user must be logged in to raise a ticket
    if (!userData?.uid) {
      customToast.info('Please login first to raise a support ticket');
      if (onLoginClick) {
        onLoginClick();
      } else {
        navigate('/', { state: { openLoginModal: true } });
      }
      return;
    }
    setSelectedCategory(categoryTitle);
    setSelectedIssue(issue);
    setShowModal(true);
  };

  const supportCategories = [
    {
      id: 'account',
      title: 'Account & Login Support',
      icon: '👤',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      issues: [
        'Trouble signing in or OTP not received',
        'Issues with Google/Microsoft sign-in',
        'Account deactivation or deletion requests'
      ]
    },
    {
      id: 'payment',
      title: 'Payment & Access Issues',
      icon: '💳',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      issues: [
        'Payment deducted but no access received',
        'Receipt or confirmation not received',
        'Plan not activated after payment'
      ]
    },
    {
      id: 'subscription',
      title: 'Subscription & Plan Support',
      icon: '📋',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      issues: [
        'Plan activation or upgrade issues',
        'Clarification on plan benefits',
        'Access duration-related queries'
      ]
    },
    {
      id: 'technical',
      title: 'Technical Support',
      icon: '⚙️',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      issues: [
        'Website not loading or errors displayed',
        'Download or feature issues',
        'Problems accessing your dashboard'
      ]
    },
    {
      id: 'privacy',
      title: 'Data & Privacy Support',
      icon: '🔒',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      issues: [
        'Requests to delete your data',
        'Questions about data handling',
        'Account removal concerns'
      ]
    }
  ];

  return (
    <div className="customer-support-page">
      {/* Hero Section */}
      <div className="support-hero">
        <div className="support-hero-content">
          <h1>We're Here to Help</h1>
          <p>At Resumeglobe, we aim to provide a smooth and hassle-free experience. If you're facing any issues related to your account, payments, or access to services, our support team is here to assist you.</p>
          <div className="support-hero-cta">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Please choose the relevant support channel below so we can help you faster.</span>
          </div>
        </div>
      </div>

      {/* My Tickets Section */}
      {userData?.uid && (
        <div className="my-tickets-section">
          <div className="support-container">
            <div className="my-tickets-header">
              <div className="my-tickets-title-row">
                <div className="my-tickets-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="13" x2="8" y2="13" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="17" x2="8" y2="17" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="10 9 9 9 8 9" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <h2 className="my-tickets-title">My Support Tickets</h2>
                  <p className="my-tickets-subtitle">Track and manage your support requests</p>
                </div>
              </div>
              {visibleTickets.length > 0 && (
                <div className="my-tickets-count">
                  <span className="ticket-count-number">{visibleTickets.length}</span>
                  <span className="ticket-count-label">Total</span>
                </div>
              )}
            </div>

            {/* Filter Tabs */}
            {visibleTickets.length > 0 && (
              <div className="ticket-filter-tabs">
                {[
                  { key: 'all', label: 'All', count: visibleTickets.length },
                  { key: 'open', label: 'Open', count: visibleTickets.filter(t => t.status === 'open').length },
                  { key: 'in-progress', label: 'In Progress', count: visibleTickets.filter(t => t.status === 'in-progress').length },
                  { key: 'closed', label: 'Closed', count: visibleTickets.filter(t => t.status === 'closed').length }
                ].map(tab => (
                  <button
                    key={tab.key}
                    className={`ticket-filter-tab ${activeFilter === tab.key ? 'active' : ''}`}
                    onClick={() => setActiveFilter(tab.key)}
                  >
                    {tab.label}
                    {tab.count > 0 && <span className="tab-count">{tab.count}</span>}
                  </button>
                ))}
              </div>
            )}

            {/* Tickets List */}
            {ticketsLoading ? (
              <div className="tickets-loading">
                <div className="tickets-loading-spinner"></div>
                <p>Loading your tickets...</p>
              </div>
            ) : filteredTickets.length > 0 ? (
              <div className="tickets-list">
                {filteredTickets.map((ticket) => {
                  const statusInfo = getStatusInfo(ticket.status);
                  const lastDesc = ticket.descriptions?.length > 0
                    ? ticket.descriptions[ticket.descriptions.length - 1]
                    : (ticket.description ? { text: ticket.description } : null);
                  const hasResponse = ticket.responses && ticket.responses.length > 0;
                  return (
                    <div
                      key={ticket.id}
                      className="ticket-card"
                      onClick={() => handleTicketClick(ticket)}
                    >
                      <div className="ticket-card-top">
                        <div className="ticket-card-id">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="7" y1="7" x2="7.01" y2="7" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          #{ticket.id?.slice(-6).toUpperCase()}
                        </div>
                        <span
                          className="ticket-card-status"
                          style={{
                            background: statusInfo.bg,
                            color: statusInfo.text,
                            borderColor: statusInfo.border
                          }}
                        >
                          <span className="status-dot-small" style={{ background: statusInfo.text }}></span>
                          {statusInfo.label}
                        </span>
                      </div>

                      <div className="ticket-card-category">{ticket.category}</div>
                      <div className="ticket-card-issue">{ticket.issue}</div>

                      {lastDesc && (
                        <div className="ticket-card-preview">
                          {typeof lastDesc === 'string'
                            ? lastDesc.substring(0, 100)
                            : lastDesc.text?.substring(0, 100)
                          }
                          {(typeof lastDesc === 'string' ? lastDesc : lastDesc.text || '').length > 100 ? '...' : ''}
                        </div>
                      )}

                      <div className="ticket-card-footer">
                        <span className="ticket-card-date">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                          </svg>
                          {formatTicketDate(ticket.createdAt)}
                        </span>
                        <div className="ticket-card-actions">
                          {hasResponse && (
                            <span className="ticket-has-response">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Response
                            </span>
                          )}
                          <span className="ticket-view-btn">
                            View
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="9 18 15 12 9 6" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : visibleTickets.length > 0 ? (
              <div className="tickets-empty-filter">
                <p>No tickets with status "{activeFilter}"</p>
              </div>
            ) : (
              <div className="tickets-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p>No support tickets yet</p>
                <span>Your submitted tickets will appear here</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Support Categories */}
      <div className="support-categories-section">
        <div className="support-container">
          <h2 className="section-title">How Can We Assist You?</h2>
          <div className="support-categories-grid">
            {supportCategories.map((category) => (
              <div key={category.id} className="support-category-card">
                <div className="category-header" style={{ background: category.gradient }}>
                  <span className="category-icon">{category.icon}</span>
                  <h3>{category.title}</h3>
                </div>
                <ul className="category-issues">
                  {category.issues.map((issue, index) => (
                    <li key={index} onClick={() => handleIssueClick(category.title, issue)} className="clickable-issue">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <div className="support-notice-section">
        <div className="support-container">
          <div className="support-notice">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 16v-4M12 8h.01" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <p>For all support-related queries, please describe the issue clearly in your email so we can assist you efficiently.</p>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="support-contact-section">
        <div className="support-container">
          <div className="contact-card">
            <div className="contact-header">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="22,6 12,13 2,6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h2>Still Need Help?</h2>
              <p>If your issue is not covered above, feel free to email us directly. Our team is committed to resolving your concerns as quickly as possible.</p>
            </div>

            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-item-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <div className="contact-item-content">
                  <span className="contact-label">Email Support</span>
                  <a href="mailto:support@resumeglobe.com" className="contact-value">support@resumeglobe.com</a>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-item-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <div className="contact-item-content">
                  <span className="contact-label">Support Availability</span>
                  <p className="contact-value">Monday – Friday | 10:00 AM – 6:00 PM (IST)</p>
                  <p className="contact-note">Note: Public holidays are excluded from support hours. Responses may be delayed on these days.</p>
                </div>
              </div>
            </div>

            <div className="contact-footer">
              <div className="thank-you-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
                Thank you for choosing Resumeglobe!
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Quick Link */}
      <div className="support-quick-links">
        <div className="support-container">
          <div className="quick-links-card">
            <h3>Looking for Quick Answers?</h3>
            <p>Check out our FAQ section for instant solutions to common questions</p>
            <button className="faq-btn" onClick={onFaqClick}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Visit FAQ Section
            </button>
          </div>
        </div>
      </div>

      {/* Support Modal */}
      <CustomerSupportModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          // Refresh tickets after closing the modal (in case a new ticket was created)
          fetchTickets();
        }}
        selectedIssue={selectedIssue}
        category={selectedCategory}
      />

      {/* Ticket Chat Modal */}
      <TicketChatModal
        show={showChatModal}
        onClose={() => {
          setShowChatModal(false);
          setSelectedTicket(null);
        }}
        ticket={selectedTicket}
        userId={userData?.uid}
        onTicketUpdated={handleTicketUpdated}
      />
    </div>
  );
};

export default CustomerSupport;
